import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PortalTopbar from "../components/PortalTopbar";
import PortalAlert from "../components/PortalAlert";
import "../components/PortalButton.css";
import "./ClinicAdminSignupPage.css";

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

function sanitizeReturnTo(value: string | null): string | null {
  const v = (value || "").trim();
  if (!v) return null;
  if (!v.startsWith("/")) return null;
  if (v.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return null;
  return v;
}

export default function ClinicAdminSignupPage() {
  const origin = useMemo(() => window.location.origin, []);
  const lockedReturnTo = useMemo(() => "/portal/request-access", []);

  const returnTo = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQs = sanitizeReturnTo(params.get("returnTo"));
      return fromQs || lockedReturnTo;
    } catch {
      return lockedReturnTo;
    }
  }, [lockedReturnTo]);

  const postLoginRedirect = useMemo(() => {
    const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
    return `${origin}/portal${qs}`;
  }, [origin, returnTo]);

  const onMicrosoftSignIn = () => {
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(
      postLoginRedirect
    )}`;
  };

  const onGoogleSignIn = () => {
    window.location.href = `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(
      postLoginRedirect
    )}`;
  };

  return (
    <div className="cas-shell">
      <PortalTopbar />

      <main className="cas-main" id="main">
        <div className="cas-card">
          <div className="cas-grid">
            <section className="cas-content" aria-labelledby="cas-title">
              <h1 id="cas-title" className="cas-title">Clinic Admin sign-up</h1>
              <p className="cas-subtitle">
                Clinic Admin access is reviewed manually. We may contact the
                clinic to verify details before granting access.
              </p>

              <section className="cas-panel" aria-labelledby="cas-how">
                <h2 id="cas-how" className="cas-panel-title">How it works</h2>
                <ol className="cas-steps">
                  <li><strong>Sign in</strong> using Microsoft or Google.</li>
                  <li>You&apos;ll be redirected to the portal and taken to <strong>Request Access</strong>.</li>
                  <li>Submit your clinic details and accept the required confirmations.</li>
                  <li>Your request shows as <strong>Pending</strong> until reviewed.</li>
                  <li>Once approved, you can manage clinic settings and invite providers.</li>
                </ol>
                <p className="cas-note">
                  Providers do not submit access requests. They join only via
                  invitation after the clinic is approved.
                </p>
              </section>

              <PortalAlert tone="danger" title="Important">
                Do not submit any patient information in the portal access flow.
              </PortalAlert>
            </section>

            <aside className="cas-signin" aria-labelledby="cas-signin-title">
              <header className="cas-signin-head">
                <h2 id="cas-signin-title" className="cas-signin-title">
                  Sign in to start
                </h2>
                <p className="cas-signin-lead">
                  Use your clinic email to sign in. You&apos;ll be taken to{" "}
                  <strong>Request Access</strong> to submit your request.
                </p>
              </header>

              <div className="cas-signin-buttons">
                <div className="cas-signin-primary">
                  <span className="cas-recommended-tag">Recommended</span>
                  <button
                    type="button"
                    onClick={onMicrosoftSignIn}
                    className="pb-btn pb-btn--dark pb-btn--full pb-btn--lg"
                  >
                    <MicrosoftIcon />
                    <span>Sign in with Microsoft</span>
                  </button>
                </div>

                <div className="cas-signin-divider" aria-hidden="true">
                  <span>or</span>
                </div>

                <button
                  type="button"
                  onClick={onGoogleSignIn}
                  className="pb-btn pb-btn--secondary pb-btn--full pb-btn--lg"
                >
                  <GoogleIcon />
                  <span>Sign in with Google</span>
                </button>
              </div>

              <div className="cas-signin-alt">
                <div className="cas-signin-alt-head">Already signed in?</div>
                <Link
                  to="/portal/request-access"
                  className="pb-btn pb-btn--secondary pb-btn--full"
                >
                  Continue to Request Access
                </Link>
                <Link
                  to="/portal/access-pending"
                  className="cas-text-link"
                >
                  Check pending status
                </Link>
              </div>
            </aside>
          </div>

          <footer className="cas-footer">
            <div className="cas-footer-copyright">
              &copy; {new Date().getFullYear()} AcceptingMD
            </div>
            <nav className="cas-footer-nav" aria-label="Legal links">
              <Link to="/public/terms" className="cas-footer-link">Terms</Link>
              <Link to="/public/privacy" className="cas-footer-link">Privacy</Link>
              <Link to="/public/contact" className="cas-footer-link">Contact</Link>
            </nav>
          </footer>
        </div>
      </main>
    </div>
  );
}
